import { z } from 'zod';
import { User } from '../../types/entities';

// ログインリクエスト/レスポンス
export interface LoginReq {
  email: string;
  password: string;
}

export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  email: z.string().email().optional(),
  icon_path: z.string().nullable()
});

export const LoginResSchema = z.object({
  token: z.string(),
  user: UserSchema,
  requires_terms_agreement: z.boolean().optional(),
  current_terms: z.object({
    id: z.string(),
    version: z.string(),
    title: z.string(),
  }).nullable().optional(),
  requires_password_change: z.boolean().optional(),
});
export type LoginRes = z.infer<typeof LoginResSchema>;

// サインアップリクエスト/レスポンス
export interface SignupReq {
  email: string;
  password: string;
  name: string;
}

export const SignupResSchema = z.object({
  token: z.string(),
  user: UserSchema
});
export type SignupRes = z.infer<typeof SignupResSchema>;

// ユーザープロファイル
export const UserProfileSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
  hospital_id: z.string().nullable(),
  hospital_name: z.string().nullable(),
  icon_path: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime()
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// プロファイル更新リクエスト
// バックエンドの UpdateProfileRequestSchema（.strict()）は name のみを許可する
export interface UpdateProfileReq {
  name?: string;
}

// プロファイル更新レスポンス（既存コードとの互換性のため）
export const ProfileUpdateResSchema = z.object({
  user: UserProfileSchema
});
export type ProfileUpdateRes = z.infer<typeof ProfileUpdateResSchema>;

// アイコン更新レスポンス（既存コードとの互換性のため）
export const IconUpdateResSchema = z.object({
  iconPath: z.string()
});
export type IconUpdateRes = z.infer<typeof IconUpdateResSchema>;

// パスワード変更リクエスト
export interface ChangePasswordReq {
  oldPassword: string;
  newPassword: string;
}

// パスワード変更レスポンス（パスワード変更後にセッション継続用トークンを再発行）
export const ChangePasswordResSchema = z.object({
  message: z.string(),
  token: z.string().optional(),
});
export type ChangePasswordRes = z.infer<typeof ChangePasswordResSchema>;

// パスワードリセットリクエスト
export interface ForgotPasswordReq {
  email: string;
}

// パスワードリセットレスポンス（管理者発行型に移行したため message のみ）
export const ForgotPasswordResSchema = z.object({
  message: z.string(),
});
export type ForgotPasswordRes = z.infer<typeof ForgotPasswordResSchema>;

// アカウント復旧リクエスト
export interface RestoreAccountReq {
  userId: string;
  password: string;
}

// 汎用成功レスポンス
export const SuccessResponseSchema = z.object({
  success: z.boolean(),
  message: z.string()
});
export type SuccessResponse = z.infer<typeof SuccessResponseSchema>;

// APIエラーレスポンス
export const ApiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  status: z.number().optional()
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

// 型変換ユーティリティ関数
// バックエンドAPIの型からフロントエンドのUser型に変換
export function toUserEntity(profile: UserProfile): User {
  return {
    id: Number(profile.id),
    email: profile.email,
    name: profile.name,
    role: profile.role,
    hospital_id: profile.hospital_id ? Number(profile.hospital_id) : undefined,
    icon_path: profile.icon_path ?? undefined,
    created_at: profile.created_at,
    updated_at: profile.updated_at
  };
} 